import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { usePostHog } from 'posthog-js/react'
import type { User as StytchUser } from "@stytch/vanilla-js";

import { stytchService } from "../utils/stytchService";
import { useSessionSetupFlow } from "../hooks/useSessionSetupFlows";
import { getSessionSpec } from "../utils/sessionEndpoints";
import { useNavUtils } from "../utils/navUtils";
import HeroCardLayout from "./layout/HeroCardLayout";
import { Size } from "../utils/types";
import { APIUser, UserStatus, getUserStatusErrorMessage, PixelStreamingExperienceDisabledClass } from "../utils/userEndpoints";
import {
  setUser,
  setActivePixelStreamingExperience,
  selectActivePixelStreamingExperience,
  setTempProfilePictureURL,
} from "../store/userSlice";
import { selectResLinkParamsId, selectOrganizationId, setOrganizationId } from "../store/experienceSlice";

import LoadingUserComponent from "./entryflow/LoadingUser";
import FinalizeCreateUserContainer from "./entryflow/FinalizeCreateUserContainer";
import LoadingSessionComponent from "./entryflow/LoadingSession";
import MicrophonePermissioningContainer from "./entryflow/MicrophonePermissioningContainer";
import UserTerminalComponent from "./entryflow/UserTerminal";
import LoadingSessionErrorComponent from "./entryflow/LoadingSessionError";
import {
  getOrCreateUser,
  findAndSetActivePixelStreamingExperienceFromUser,
} from "./entryflow/userFlowUtils";
import { isHttpError } from "../api_middleware";

import "../styles/components/SessionCreationLobby.css";

type LobbyState =
  | "checkinguser"
  | "finalizinguser"
  | "userterminal"
  | "microphonepermissioning"
  | "sessioncreating"
  | "sessionloadingerror";

const SessionCreationLobby: React.FC = () => {
  const [lobbyState, setLobbyState] = useState<LobbyState>("checkinguser");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const rawSize = sessionStorage.getItem("loginCardSize");
  const initialCardSize = rawSize ? JSON.parse(rawSize) : null;
  const [contentSize, setContentSize] = useState<Size | null>(initialCardSize);

  const didCheckUser = useRef(false);

  const { handleManualSessionSetup } = useSessionSetupFlow();
  const { goNav, logoutReset } = useNavUtils();
  const location = useLocation();
  const dispatch = useDispatch();
  const activePixelStreamingExperience = useSelector(selectActivePixelStreamingExperience);
  const resLinkParamsId = useSelector(selectResLinkParamsId);
  const reduxOrganizationId = useSelector(selectOrganizationId);

  const [stytchUser, setStytchUser] = useState<StytchUser | null>(null);
  const posthog = usePostHog();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // If the resulting user is kosher, then extract the organization id,
  // save the user to the redux store, and advance to the next state depending
  // on the user status. Otherwise go to the userterminal state.
  const handleCompletedUser = useCallback(
    (user: APIUser) => {

      console.log("[SessionCreationLobby] handleCompletedUser, user:", user);

      dispatch(setUser(user));

      posthog?.identify(user.id, {
          email: user.email,
          first_name: user.name?.first_name,
          last_name: user.name?.last_name,
      });

      switch (user.status) {
        case UserStatus.ACTIVE:
          setLobbyState("microphonepermissioning");
          break;
        case UserStatus.ONBOARDING:
          setLobbyState("finalizinguser");
          break;
        case UserStatus.WAITLISTED:
        case UserStatus.SUSPENDED:
        case UserStatus.BANNED:
          setStatusMessage(getUserStatusErrorMessage(user.status));
          setLobbyState("userterminal");
          return;
      }

      let experience = findAndSetActivePixelStreamingExperienceFromUser(
        user,
        resLinkParamsId
      );

      if (!experience) {
        setStatusMessage("Invalid link. Please check the link and try again.");
        setLobbyState("userterminal");
        dispatch(setActivePixelStreamingExperience(null));
        return;
      }

      if (!experience.is_enabled) {
        let statusMsg = "";
        if (experience.disabled_rationale?.error_text) {
          statusMsg = experience.disabled_rationale.error_text;
        } else if (experience.disabled_rationale?.disabled_class) {
          switch (experience.disabled_rationale.disabled_class) {
            case PixelStreamingExperienceDisabledClass.LINK_CONSUMPTION_EXHAUSTED:
              statusMsg =
                "This is a one-time experience, so repeating it isn't possible. Thanks for being part of it!";
              break;
            default:
              statusMsg = "You do not have access to this experience.";
          }
        } else {
          statusMsg = "You do not have access to this experience.";
        }
        setStatusMessage(statusMsg);
        setLobbyState("userterminal");
        dispatch(setActivePixelStreamingExperience(null));
        return;
      }

      if (experience.organization_id) {
        dispatch(setOrganizationId(experience.organization_id))
      }
      dispatch(setActivePixelStreamingExperience(experience));
    },
    [dispatch, params, posthog]
  );

  // Checks whether the user exists. If they don't, create them.
  // If the user is active, go to the next step
  // (microphone permissioning). If not, go to "finalizing user",
  // in order to gather name, linkedin, etc.
  useEffect(() => {
    console.log("[SessionCreationLobby] starting initial effect");

    if (didCheckUser.current) return;

    const su = stytchService.getUserSync();

    console.log("[SessionCreationLobby] Fetched Stytch user:", su);
    if (!su) return;
    setStytchUser(su);

    didCheckUser.current = true;
    setStatusMessage(null);

    const ensureExistingUser = async () => {
      const userId = (su.trusted_metadata as { user_id?: string })?.user_id;
      const email = su.emails?.[0]?.email;
      const profilePicture = su.providers?.[0]?.profile_picture_url;

      console.log("[SessionCreationLobby] userId:", userId, " email:", email, "resLinkParamsId:", resLinkParamsId, "reduxOrganizationId:", reduxOrganizationId, " profilePicture:", profilePicture);

      try {
        const user = await getOrCreateUser(userId, email, resLinkParamsId, reduxOrganizationId);
        console.log("[SessionCreationLobby] saving profilePicture:", profilePicture);
        dispatch(setTempProfilePictureURL(profilePicture));
        handleCompletedUser(user);
      } catch (err) {
        console.error("[SessionCreationLobby] Error from getOrCreateUser:", err);
        if (isHttpError(err)) {
          if (err.status === 401) {
            // The 'detail' field isn't very user-friendly. Use our own text for now.
            await logoutReset(false, "Failed to log in. Please try again.");
            return;
          }
          setStatusMessage(err.message || "Failed to create user. Please try again.");
        } else if (err instanceof Error) {
          setStatusMessage(err.message);
        } else {
          setStatusMessage("Failed to create user. Please try again.");
        }
        setLobbyState("userterminal");
      }
    };

    ensureExistingUser();
  }, [dispatch, handleCompletedUser, resLinkParamsId, reduxOrganizationId]);

  // Add ResizeObserver to handle dynamic content changes
  useEffect(() => {
    if (!contentRef.current || lobbyState === "checkinguser") return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContentSize({
          width: width,
          height: height,
        });
      }
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [lobbyState]);

  const launchSession = useCallback(async () => {
    const createTokenJwt = params.get("create_token_jwt") || undefined;
    const resLinkParamsId = activePixelStreamingExperience?.res_link_params_id || undefined;
    const sessionSpec = getSessionSpec(reduxOrganizationId || "", undefined, createTokenJwt, resLinkParamsId);
    setStatusMessage(null);
    setLobbyState("sessioncreating");
    try {
      await handleManualSessionSetup(sessionSpec);
      goNav("/avatarstreamer");
    } catch (err) {
      if (err instanceof Error) {
        setStatusMessage(err.message || "Failed to create session.");
      } else {
        setStatusMessage("Unknown error creating session.");
      }
      setLobbyState("sessionloadingerror");
    }
  }, [goNav, handleManualSessionSetup, params, activePixelStreamingExperience, reduxOrganizationId]);

  const handleMicStarted = () => {
    launchSession();
  };

  // --- Render based on lobbyState ---

  if (lobbyState === "checkinguser" || !stytchUser) {
    return (
      <HeroCardLayout>
        <div
          style={{
            minHeight: contentSize?.height || "auto",
            minWidth: contentSize?.width || "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LoadingUserComponent />
        </div>
      </HeroCardLayout>
    );
  }

  if (lobbyState === "userterminal") {
    return (
      <HeroCardLayout>
        <UserTerminalComponent reason={statusMessage || ""} />
      </HeroCardLayout>
    );
  }

  if (lobbyState === "microphonepermissioning") {
    return (
      <HeroCardLayout>
        <MicrophonePermissioningContainer 
          onMicrophonePermissioned={handleMicStarted} 
          botName={activePixelStreamingExperience?.bot_name} />
      </HeroCardLayout>
    );
  }

  if (lobbyState === "sessioncreating") {
    return <LoadingSessionComponent />;
  }

  if (lobbyState === "sessionloadingerror") {
    return <LoadingSessionErrorComponent 
      errorMessage={statusMessage} 
      onRetry={launchSession} />;
  }

  // lobbyState === "finalizinguser"
  return (
    <HeroCardLayout>
      <FinalizeCreateUserContainer
        onSubmitSuccess={handleCompletedUser}
        resLinkParamsId={resLinkParamsId}
        botName={activePixelStreamingExperience?.bot_name}
        propFirstName={stytchUser?.name?.first_name}
        middleName={stytchUser?.name?.middle_name}
        propLastName={stytchUser?.name?.last_name}
        profilePictureUrl={stytchUser?.providers?.[0]?.profile_picture_url}
      />
    </HeroCardLayout>
  );
};

export default SessionCreationLobby;
